import type { BoardContent, Command, Hazard, HazardType, PlayerId, PlayerState, Snapshot, SimStatus } from './types';
import type { DifficultyParams } from './config';
import { LIVES_START, SCORING, SCANS_PER_BOARD } from './config';
import { Rng } from './rng';
import { EventLog } from './eventLog';
import { buildBoard, type BoardState } from './board';
import { moveAndMaybeEat, munchHere, applyScan } from './rules';
import { spawnHazard, stepHazard, resolveHazardHits, purgeStun } from './hazards';
import { AIController } from './ai';

const SUPPORTED_HAZARDS = new Set<HazardType>(['crawler', 'hunter', 'liar', 'scrambler']); // all four live (M3)
const MAX_HAZARDS = 6;

export interface SimOptions {
  content: BoardContent;
  params: DifficultyParams;
  seed: number;
  aiEnabled: boolean;
  /**
   * Extra SCAN charges granted to the HUMAN at board start (e.g. a rewarded "+1 scan on retry").
   * Defaults to 0, so a normal Sim is byte-identical to before — sim:smoke never sets it. It only
   * raises the human's starting scansLeft (a player-state count consumed by applyScan); it adds NO
   * RNG draw / tick step / event type, so the determinism contract is unchanged.
   */
  extraScans?: number;
}

function makePlayer(id: PlayerId, isAI: boolean, pos: number, extraScans = 0): PlayerState {
  return {
    id,
    isAI,
    pos,
    score: 0,
    lives: LIVES_START,
    combo: 0,
    stunnedUntilMs: 0,
    eatenCorrect: 0,
    eatenDecoy: 0,
    armedTraversals: 0,
    scansLeft: SCANS_PER_BOARD + Math.max(0, Math.floor(extraScans)),
    surgeUntilMs: 0,
    alive: true,
  };
}

/** One board's authoritative simulation. The AI is just PlayerId 1 — a remote player drops in identically. */
export class Sim {
  readonly seed: number;
  readonly aiEnabled: boolean;
  readonly log = new EventLog();
  readonly state: BoardState;
  readonly players: PlayerState[] = [];
  readonly hazards: Hazard[] = [];

  timeMs = 0;
  timeLeftMs: number;
  quotaMetBy: PlayerId | null = null;
  status: SimStatus = 'playing';
  winner: PlayerId | null = null;

  private rng: Rng;
  private queued = new Map<PlayerId, Command>();
  private ai?: AIController;
  private hazardRng = new Map<number, Rng>();
  private hazardTypesCycle: HazardType[];
  private hazardCursor = 0;
  private nextHazardAtMs: number; // first-spawn grace — set from params in the constructor (see below)
  private hazardSeq = 0;

  constructor(opts: SimOptions) {
    this.seed = opts.seed;
    this.aiEnabled = opts.aiEnabled;
    this.rng = new Rng(opts.seed);
    this.state = buildBoard(opts.content, opts.params, this.rng.fork(101));
    this.timeLeftMs = opts.params.softTimerMs;
    // 2026-07-03 cold-open grace (owner-feedback / visual-probe finding): the first hazard used to
    // spawn a flat 1500ms in — before a cold player has read the rule or cleared any safe ground, a
    // cheap opening chase-death that fed the "impossible" feel. Tie the FIRST spawn to the cadence
    // (40%, floored at 5000ms): base 15000→6000, board-5 12000→5000, SWARM 5000→5000 (twist keeps its
    // bite). Integer, no new RNG site — subsequent spawns keep the existing per-interval cadence.
    this.nextHazardAtMs = Math.max(5000, Math.round(opts.params.hazardIntervalMs * 0.4));

    this.players.push(makePlayer(0, false, this.state.humanStart, opts.extraScans ?? 0));
    if (opts.aiEnabled) {
      this.players.push(makePlayer(1, true, this.state.aiStart));
      this.ai = new AIController(1, opts.params.aiTier, this.rng.fork(202));
      this.ai.onBoardStart(this.state);
    }

    // Lead with the most-advanced hazard so the signature liar/scrambler actually appear before a
    // (often short, 7-15s) board ends — otherwise they'd be scheduled past the board's lifetime.
    // An empty list is intentional for beginner/training boards: no hazards spawn.
    this.hazardTypesCycle = opts.params.hazardTypes.filter((t) => SUPPORTED_HAZARDS.has(t)).reverse();

    this.log.push('boardStart', 0, { value: this.state.quota });
  }

  enqueue(playerId: PlayerId, cmd: Command): void {
    if (this.status === 'playing') this.queued.set(playerId, cmd);
  }

  /** Advance the sim by dtMs, split into `steps` equal sub-steps (headless tests use small steps). */
  tick(dtMs: number, steps = 1): void {
    const d = dtMs / steps;
    for (let s = 0; s < steps; s++) {
      if (this.status !== 'playing') break;
      this.advance(d);
    }
  }

  private advance(dt: number): void {
    this.timeMs += dt;
    this.timeLeftMs -= dt;

    for (const c of this.state.cells) {
      if (c.armed && this.timeMs >= c.armedUntilMs) {
        c.armed = false;
        this.log.push('armExpire', this.timeMs, { cell: c.id });
      }
      if (c.revealedUntilMs > 0 && this.timeMs >= c.revealedUntilMs) {
        c.revealedUntilMs = 0;
        this.log.push('revealExpire', this.timeMs, { cell: c.id });
      }
      if (c.lying && this.timeMs >= c.lyingUntilMs) {
        c.displayText = c.text; // revert the Liar's relabel (true text)
        c.lying = false;
      }
    }

    while (this.hazardTypesCycle.length > 0 && this.timeMs >= this.nextHazardAtMs) {
      if (this.hazards.length < MAX_HAZARDS) {
        const type = this.hazardTypesCycle[this.hazardCursor % this.hazardTypesCycle.length] as HazardType;
        this.hazardCursor++;
        const id = this.hazardSeq++;
        const r = this.rng.fork(300 + id);
        const h = spawnHazard(this.state, type, id, this.players, this.timeMs, r, this.log);
        this.hazards.push(h);
        this.hazardRng.set(id, r);
      }
      this.nextHazardAtMs += this.state.params.hazardIntervalMs;
    }

    if (this.ai && this.players[1]) {
      // Rubber-band for a FELT close race: modulate effort (speed) AND a BOUNDED, throttled
      // knowledge catch-up/ease-off (capped per board, never omniscient) so neither side blows out.
      // Behind -> faster + nudge a belief toward truth; ahead -> slower + nudge one toward a mistake.
      const gap = this.players[1].score - (this.players[0]?.score ?? 0);
      if (gap < -250) {
        this.ai.setSpeedMul(0.5);
        this.ai.nudgeBelief(this.state, true, this.timeMs);
      } else if (gap > 250) {
        this.ai.setSpeedMul(1.7);
        this.ai.nudgeBelief(this.state, false, this.timeMs);
      } else {
        this.ai.setSpeedMul(1.0);
      }
      const cmd = this.ai.decide(this.state, this.players[1], this.timeMs);
      if (cmd) this.queued.set(1, cmd);
    }

    for (const p of this.players) {
      const cmd = this.queued.get(p.id);
      this.queued.delete(p.id);
      if (!cmd || !p.alive || this.timeMs < p.stunnedUntilMs) continue;
      this.applyCommand(p, cmd);
    }

    for (const h of this.hazards) {
      stepHazard(this.state, h, this.players, this.timeMs, this.hazardRng.get(h.id) as Rng, this.log);
    }
    // resolveHazardHits may DESTROY hazards (a CHOMP SURGE purge splices them out of `this.hazards`).
    resolveHazardHits(this.state, this.hazards, this.players, this.timeMs, this.log);
    // Reconcile the per-hazard RNG map with the live set so a purged hazard's forked stream is GC'd.
    // Pure bookkeeping: no RNG draw, no event, no tick-order effect — determinism/log-hash unchanged.
    if (this.hazardRng.size > this.hazards.length) {
      const liveIds = new Set(this.hazards.map((h) => h.id));
      for (const id of this.hazardRng.keys()) if (!liveIds.has(id)) this.hazardRng.delete(id);
    }

    this.checkEnd();
  }

  private applyCommand(p: PlayerState, cmd: Command): void {
    let eff;
    if (cmd.type === 'move') eff = moveAndMaybeEat(this.state, p, cmd.dir, this.timeMs, this.log);
    else if (cmd.type === 'munch') eff = munchHere(this.state, p, this.timeMs, this.log);
    else if (cmd.type === 'scan') eff = applyScan(this.state, p, this.timeMs, this.log);
    else return;

    if (eff.timeRefundMs > 0) this.timeLeftMs += eff.timeRefundMs;
    if (eff.purgeCell !== null) {
      this.log.push('purgePulse', this.timeMs, { player: p.id, cell: eff.purgeCell });
      purgeStun(this.state, this.hazards, eff.purgeCell, this.timeMs);
    }
    if (this.quotaMetBy === null && p.eatenCorrect >= this.state.quota) {
      this.quotaMetBy = p.id;
      this.log.push('quotaMet', this.timeMs, { player: p.id });
    }
  }

  private checkEnd(): void {
    if (this.status !== 'playing') return;
    const human = this.players[0] as PlayerState;
    const allDead = this.aiEnabled && this.players.every((p) => !p.alive);
    const ended = this.quotaMetBy !== null || this.timeLeftMs <= 0 || !human.alive || allDead;
    if (!ended) return;

    if (human.eatenDecoy === 0 && human.alive && human.eatenCorrect === this.state.correctCount) {
      human.score += SCORING.perfectBoardBonus;
      this.log.push('perfect', this.timeMs, { player: 0, value: SCORING.perfectBoardBonus });
    }

    let winner: PlayerId | null;
    if (this.aiEnabled) {
      const alive = this.players.filter((p) => p.alive);
      const pool = alive.length > 0 ? alive : this.players;
      winner = pool.reduce((a, b) => (b.score > a.score ? b : a)).id;
    } else {
      winner = human.alive && this.quotaMetBy === 0 ? 0 : null;
    }

    this.winner = winner;
    if (winner === 0) {
      this.status = 'won';
      this.log.push('boardWin', this.timeMs, { player: 0 });
    } else {
      this.status = 'lost';
      this.log.push('boardLose', this.timeMs, { player: winner ?? -1 });
    }
    if (this.timeLeftMs < 0) this.timeLeftMs = 0;
  }

  snapshot(): Snapshot {
    return {
      timeMs: this.timeMs,
      timeLeftMs: Math.max(0, Math.round(this.timeLeftMs)),
      rows: this.state.rows,
      cols: this.state.cols,
      rule: this.state.rule,
      topic: this.state.topic,
      cells: this.state.cells,
      players: this.players,
      hazards: this.hazards,
      quota: this.state.quota,
      quotaMetBy: this.quotaMetBy,
      status: this.status,
      winner: this.winner,
    };
  }
}
